import { useEffect } from 'react';
import './PyroBallAnimation.css';

/**
 * PROFESSIONAL PYRO BALL ANIMATION
 * Fire-type physical move - kicks a blazing fireball at the opponent
 * 
 * Key Features:
 * - Fire-type physical attack (120 base power, 90% accuracy)
 * - 10% chance to burn
 * - Cinderace signature move with soccer-kick fireball projectile
 * - Professional gaming quality with blazing trail
 */
export default function PyroBallAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    const elements = [];

    // PHASE 1: Fire Ignition at Feet (0-500ms)
    playerEl.classList.add('pyro-ball-user-charge');

    const ignitionAura = document.createElement('div');
    ignitionAura.className = 'pyro-ball-ignition-aura';
    ignitionAura.style.left = `${playerX - 60}px`;
    ignitionAura.style.top = `${playerY + 10}px`;
    container.appendChild(ignitionAura);
    elements.push(ignitionAura);

    // Fire sparks at player's feet
    for (let i = 0; i < 10; i++) {
      const spark = document.createElement('div');
      spark.className = 'pyro-ball-ignition-spark';
      spark.style.left = `${playerX + (Math.random() - 0.5) * 50 - 3}px`;
      spark.style.top = `${playerY + 15 + Math.random() * 20 - 3}px`;
      spark.style.animationDelay = `${i * 40}ms`;
      container.appendChild(spark);
      elements.push(spark);
    }

    // Forming fireball at player
    const formingBall = document.createElement('div');
    formingBall.className = 'pyro-ball-forming';
    formingBall.style.left = `${playerX - 20}px`;
    formingBall.style.top = `${playerY - 20}px`;
    container.appendChild(formingBall);
    elements.push(formingBall);

    // PHASE 2: Fireball Launch (500-1000ms)
    const phase2Timer = setTimeout(() => {
      // Remove forming ball
      if (formingBall.parentNode) formingBall.remove();

      // Create the blazing fireball projectile
      const fireball = document.createElement('div');
      fireball.className = 'pyro-ball-projectile';
      fireball.style.left = `${playerX}px`;
      fireball.style.top = `${playerY}px`;
      fireball.style.setProperty('--dx', `${dx}px`);
      fireball.style.setProperty('--dy', `${dy}px`);
      container.appendChild(fireball);
      elements.push(fireball);

      // Inner core of fireball
      const fireCore = document.createElement('div');
      fireCore.className = 'pyro-ball-core';
      fireball.appendChild(fireCore);

      // Blazing trail behind fireball
      for (let i = 0; i < 15; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'pyro-ball-trail';
          const progress = i / 15;
          const tx = playerX + dx * progress + (Math.random() - 0.5) * 15;
          const ty = playerY + dy * progress + (Math.random() - 0.5) * 15;
          trail.style.left = `${tx - 8}px`;
          trail.style.top = `${ty - 8}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 30);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Ember particles along path
      for (let i = 0; i < 20; i++) {
        const emberTimer = setTimeout(() => {
          const ember = document.createElement('div');
          ember.className = 'pyro-ball-ember';
          const progress = i / 20;
          ember.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 25 - 4}px`;
          ember.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 25 - 4}px`;
          container.appendChild(ember);
          elements.push(ember);
        }, i * 22);
        elements.push({ timer: emberTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Blazing Impact (1000-1500ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('pyro-ball-opponent-hit');

      // Massive fire explosion
      const explosion = document.createElement('div');
      explosion.className = 'pyro-ball-explosion';
      explosion.style.left = `${opponentX - 110}px`;
      explosion.style.top = `${opponentY - 110}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Fire burst particles
      for (let i = 0; i < 22; i++) {
        const burstAngle = (i / 22) * 360;
        const burstDist = 60 + Math.random() * 50;
        const burst = document.createElement('div');
        burst.className = 'pyro-ball-burst-particle';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 12}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      // Fire shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'pyro-ball-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Burn Aftermath (1500-1900ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('pyro-ball-user-charge');

      // Lingering flames on opponent
      for (let i = 0; i < 6; i++) {
        const flame = document.createElement('div');
        flame.className = 'pyro-ball-linger-flame';
        flame.style.left = `${opponentX + (Math.random() - 0.5) * 60 - 8}px`;
        flame.style.top = `${opponentY + (Math.random() - 0.5) * 40 - 8}px`;
        flame.style.animationDelay = `${i * 50}ms`;
        container.appendChild(flame);
        elements.push(flame);
      }
    }, 1500);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1900ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('pyro-ball-user-charge');
      opponentEl.classList.remove('pyro-ball-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1900);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('pyro-ball-user-charge');
      opponentEl.classList.remove('pyro-ball-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

PyroBallAnimation.moveType = 'fire';
